<script lang="ts" setup>
  import { useViewQuery, useUrlGenerator } from '@othercommerce/foundation';
  import { computed, type PropType } from 'vue';
  import type { Module, Section } from '@storefront/types';

  defineOptions({
    inheritAttrs: false,
  });

  const props = defineProps({
    module: { type: Object as PropType<Module>, required: true },
    category: { type: Object, required: false },
    categories: { type: Object, required: true },
    sections: { type: Array as PropType<Section[]>, required: true },
  });

  const query = useViewQuery();
  const url = useUrlGenerator();

  const clearFilters = computed(() => {
    return url.generate(window.location.pathname);
  });

  const nested = computed(() => {
    return !!props.categories.parent;
  });

  const filtered = computed(() => {
    return query.value && Object.values(query.value).length > 0;
  });

  const classes = computed(() => {
    return {
      'shop-categories--children': nested.value,
    };
  });
</script>
<template>
  <div :class="classes" class="shop-categories">
    <div v-if="nested || filtered" class="shop-categories__actions">
      <SfButton v-if="nested" :href="$url(categories.parent.url, query)" small transparent>
        <template #before>
          <SfIconChevronLeft aria-hidden="true" class="shop-categories__icon-back" />
        </template>
        <template #default>
          {{ categories.parent.name }}
        </template>
      </SfButton>
      <SfButton v-if="filtered" :href="clearFilters" small transparent>
        <template #before>
          <SfIconClose aria-hidden="true" class="shop-categories__icon-close" />
        </template>
        <template #default>
          {{ $t('SHOP.PRODUCT.ACTION.FILTER_CLEAN') }}
        </template>
      </SfButton>
    </div>
    <div class="section-before">
      <SectionRenderer :sections="sections" placement="before" />
    </div>
    <div class="shop-categories__content">
      <CategoryCard v-for="category in categories.children" :category="category" />
    </div>
    <div class="section-after">
      <SectionRenderer :sections="sections" placement="after" />
    </div>
  </div>
</template>
<style lang="scss">
  :root {
    --shop-products-grid-categories-grid-template-columns: 2;
  }
</style>
<style lang="scss" scoped>
  .shop-categories {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 2rem;
    width: 100%;
    margin: 1rem auto;

    &__actions {
      display: flex;
      flex-direction: row;
      gap: 0.875rem;
    }

    &__content {
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
      gap: 2rem;
      width: 100%;
    }

    &__icon-back, &__icon-close {
      font-size: 0.75rem;
    }

    .section-before {
      width: 100%;
      padding-bottom: 1rem;
    }

    .section-after {
      width: 100%;
      padding-top: 1rem;
    }
  }

  @media(max-width: 992px) {
    .shop-categories {
      margin: 0.5rem auto 1rem;
      width: 95%;
      gap: 1rem;

      &__content {
        gap: 0.75rem;
      }

      &--children .shop-categories__content {
        margin-top: 1rem;
      }
    }
  }

  @media(max-width: 576px) {
    .shop-categories__content {
      grid-template-columns: repeat(2, 1fr);
    }
  }
</style>
